/**
 *
 * @module accessStorage
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Access from './access';
import AccessContextState from './accessContextState';

const Util = Services.Util;
const Storage = Services.Storage;
const AccessContext = Services.Token.AccessContext;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class AccessStorage
 * @desc Provides a storage mechanism for storing Access.
 *
 */
export default class AccessStorage {

    /**
     *
     * @param {String} clientId
     * @param {String} environment
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.Access} [access=null]
     * @param {SDK.Services.Storage} [storage=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(clientId, environment, logger = Logging, access = null, storage = null) {

        const errorMessage = {
            ctor: 'AccessStorage(clientId, environment, logger, access, storage)',
            get clientId() {
                return `${this.ctor} clientId should be a String with length`;
            },
            get environment() {
                return `${this.ctor} environment should be a String with length`;
            },
            get access() {
                return `${this.ctor} needs SDK.Token.Access instance`;
            },
            get storage() {
                return `${this.ctor} needs SDK.Services.Storage instance`;
            }
        };

        nonEmptyString(clientId, errorMessage.clientId, InvalidArgumentException);
        nonEmptyString(environment, errorMessage.environment, InvalidArgumentException);

        if (Check.assigned(access)) {
            instanceStrict(access, Access, errorMessage.access, InvalidArgumentException);
        }

        if (Check.assigned(storage)) {
            instanceStrict(storage, Storage, errorMessage.storage, InvalidArgumentException);
        }

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.clientId = clientId;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.environment = environment;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Access|Null}
         *
         */
        this.access = null;

        /**
         *
         * @access private
         * @type {SDK.Services.Storage}
         *
         */
        this.storage = storage || new Storage(this.logger);

        /**
         *
         * @access private
         * @type {String}
         * @desc cache key scoped under client ID and environment to prevent clashes,
         * maintains the same structure as all other cacheKey(s) in the SDK
         *
         */
        this.cacheKey = `
            __bam_sdk_access--${this.clientId}_${this.environment}
        `.replace(/(?:\n\s+)/g, '');

        /**
         *
         * @access private
         * @type {String}
         * @desc legacy (version 2) cacheKey reference
         *
         */
        this.cacheKeyLegacy = `
            __bam_sdk_authorization_context--${this.clientId}_${this.environment}
        `.replace(/(?:\n\s+)/g, '');

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc helps determine if you are migrating from a v2 -> v3 token
         *
         */
        this.isLegacyToken = false;

        this.logger.log(AccessStorage.name, 'Created.');

        /**
         *
         * @note save Access if passed in as an argument, otherwise
         * attempt to fetch from the {SDK.Services.Storage} implementation
         *
         */
        if (Check.assigned(access)) {

            this.saveAccess(access);

        } else {

            this.getAccessFromStorage();

        }

    }

    /**
     *
     * @access private
     * @param {Access} access
     * @desc stores Access in Storage
     * @returns {Promise<Void>}
     *
     */
    saveAccess(access) {

        const errorMessage = {
            method: 'AccessStorage.saveAccess(access)',
            get access() {
                return `${this.method} needs Access instance`;
            }
        };

        instanceStrict(access, Access, errorMessage.access, InvalidArgumentException);

        const { cacheKey, logger, storage } = this;

        logger.info(AccessStorage.name, `Save Access to Storage using key: "${cacheKey}".`);

        this.access = access;

        return storage.set(cacheKey, this.access);

    }

    /**
     *
     * @access private
     * @desc returns Access from Storage
     * @returns {Access}
     *
     */
    getAccess() {

        const { logger } = this;

        logger.log(AccessStorage.name, 'Get cached Access.');

        return this.access;

    }

    /**
     *
     * @access private
     * @desc deletes the current Access from Storage
     * @returns {Promise<Void>}
     *
     */
    clear() {

        const { cacheKey, logger, storage } = this;

        logger.warn(AccessStorage.name, `Clearing cached Access for key: "${cacheKey}".`);

        this.access = null;

        return storage.remove(cacheKey);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc executed a single time on AccessStorage instantiation
     * checks Storage to determine if Access was previously stored,
     * should fail silently if no Access found
     * @note when the primary cache key is not found, the legacy key is
     * checked for migration purposes (the old token is exchanged for a new one)
     * @returns {Promise<Access>}
     *
     */
    getAccessFromStorage() {

        const { cacheKey, logger, storage } = this;

        logger.info(AccessStorage.name, `Get Access from Storage using cache key: "${cacheKey}".`);

        return storage.get(cacheKey).catch(() => {

            return Promise.resolve(this.access);

        }).then((access) => {

            const context = Util.getSafe(() => access.context);
            const contextStateModes = Util.getSafe(() => access.contextState.modes);

            if (Check.assigned(context)) {

                this.access = new Access(
                    new AccessContext(context),
                    new AccessContextState(contextStateModes)
                );

                return Promise.resolve(this.access);

            }

            return this.getLegacyAccessFromStorage();

        });

    }

    /**
     *
     * @access private
     * @desc grabs the legacy access context from Storage for migration purposes
     * @note for migration purposes, the refreshToken is required, however both tokens
     * are included below for completness
     * @note the expiresIn value is set to 0 to force an immediate refresh
     * @returns {Promise<Access>}
     *
     */
    getLegacyAccessFromStorage() {

        const { cacheKeyLegacy, logger, storage } = this;

        logger.info(AccessStorage.name, `Get legacy Access from Storage using cache key: "${cacheKeyLegacy}".`);

        return storage.get(cacheKeyLegacy).catch(() => {

            return Promise.resolve(this.access);

        }).then((context) => {

            const token = Util.getSafe(() => context.accessToken);
            const refreshToken = Util.getSafe(() => context.refreshToken);
            const expiresIn = 0;

            if (Check.assigned(refreshToken)) {

                this.isLegacyToken = true;
                this.access = new Access(
                    new AccessContext({
                        token,
                        refreshToken,
                        expiresIn
                    }),
                    new AccessContextState()
                );

            }

            return storage.remove(cacheKeyLegacy).then(() => {
                return Promise.resolve(this.access);
            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/token/accessStorage.js